// "Grab this screen right now" for the copilot's bug reporter — the same approach as Chirply's.
// Uses the standard getDisplayMedia API (the only way a page can see its own pixels), paints one
// frame onto a canvas and stops sharing immediately. Nothing leaves the browser until the report
// is sent. `prepare`/`cleanup` let the floating assistant hide itself for the grab so it isn't
// stamped across its own screenshot.
import { stamp } from "./attachment-rules";

export function isScreenshotSupported(): boolean {
  return (
    typeof window !== "undefined" &&
    typeof navigator !== "undefined" &&
    typeof navigator.mediaDevices?.getDisplayMedia === "function" &&
    typeof HTMLCanvasElement !== "undefined" &&
    typeof HTMLCanvasElement.prototype.toBlob === "function"
  );
}

/** "cancelled" is a choice (say nothing); "error" broke (say so); "captured" goes to the editor. */
export type CaptureOutcome =
  | { status: "captured"; file: File }
  | { status: "cancelled" }
  | { status: "unsupported" }
  | { status: "error"; message: string };

export interface CaptureOptions {
  /** Runs once the stream is live, just before the frame is read — hide floating UI here. */
  prepare?: () => Promise<void> | void;
  /** Always runs after the grab to restore anything hidden. */
  cleanup?: () => void;
}

/** Chromium's "offer this tab first" hint, which the DOM types don't know yet. */
type DisplayOptions = DisplayMediaStreamOptions & { preferCurrentTab?: boolean; selfBrowserSurface?: "include" | "exclude" };

export async function captureScreenshot(opts: CaptureOptions = {}): Promise<CaptureOutcome> {
  if (!isScreenshotSupported()) return { status: "unsupported" };
  let stream: MediaStream;
  try {
    const constraints: DisplayOptions = { video: { frameRate: 4 }, audio: false, preferCurrentTab: true, selfBrowserSurface: "include" };
    stream = await navigator.mediaDevices.getDisplayMedia(constraints);
  } catch (err) {
    const name = (err as { name?: string } | null)?.name;
    if (name === "NotAllowedError" || name === "AbortError") return { status: "cancelled" };
    return { status: "error", message: "Your browser wouldn't share the screen. Snip it and paste it in instead." };
  }
  try {
    const file = await grabFrame(stream, opts);
    return file ? { status: "captured", file } : { status: "error", message: "The screen came back blank — snip it and paste it in instead." };
  } catch {
    return { status: "error", message: "That screenshot couldn't be captured." };
  } finally {
    // A live track keeps Chrome's "sharing your screen" bar up — stop it the moment we have the frame.
    for (const track of stream.getTracks()) track.stop();
    opts.cleanup?.();
  }
}

async function grabFrame(stream: MediaStream, opts: CaptureOptions): Promise<File | null> {
  const video = document.createElement("video");
  video.muted = true;
  video.playsInline = true;
  video.srcObject = stream;
  try {
    await video.play();
  } catch {
    /* a detached, muted video can refuse to play yet still expose frames */
  }
  await waitForFrame(video);
  // Hide the assistant, then let the compositor catch up: the stream trails the DOM by a frame or two.
  await opts.prepare?.();
  await nextFrame();
  await nextFrame();
  await new Promise((r) => window.setTimeout(r, 150));
  const width = video.videoWidth;
  const height = video.videoHeight;
  if (!width || !height) return null;
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d");
  if (!ctx) return null;
  ctx.drawImage(video, 0, 0, width, height);
  video.pause();
  video.srcObject = null;
  const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, "image/png"));
  return blob ? new File([blob], `screenshot-${stamp()}.png`, { type: "image/png" }) : null;
}

function waitForFrame(video: HTMLVideoElement): Promise<void> {
  if (video.videoWidth > 0 && video.videoHeight > 0) return Promise.resolve();
  return new Promise((resolve) => {
    let settled = false;
    const done = () => {
      if (settled) return;
      settled = true;
      video.removeEventListener("loadeddata", done);
      resolve();
    };
    video.addEventListener("loadeddata", done);
    window.setTimeout(done, 1500);
  });
}

export const nextFrame = (): Promise<void> => new Promise((r) => requestAnimationFrame(() => r()));
